export interface OfferFeature {
  enabled: boolean;
  mobileRegistrationRequired: boolean;
  checkOffersUserAvailability: boolean;
  identificationStyle: OfferIdentificationStyle;
  /** Club registration PDF shared by users of this company or estate. */
  registrationTerms?: OfferRegistrationTerms | null;
}

export enum OfferIdentificationStyle {
  FLIKO = 'FLIKO',
  ACC = 'ACC'
}

export interface OffersUserAvailabilityData {
  [companyIdOrEstateId: string]: true | null;
}

export interface CompanyUserData {
  [userId: string]: true;
}

export interface OfferLocalizedText {
  [languageCode: string]: string;
}

export interface OfferRegistrationTerms {
  /** HTTPS download URLs for the PDF, keyed by language. */
  url: OfferLocalizedText;
  /** Optional document link labels, keyed by language. */
  urlTitle?: OfferLocalizedText | null;
}

export const OFFER_CURRENCIES = ['PLN', 'EUR', 'USD'];

export interface OfferData {
  id: string;
  /** EmailLabs-safe identifier used only to correlate delivery reports. */
  emailTagId?: string;
  /** one of estateId or companyId always exists */
  estateId: string | null;
  /** one of estateId or companyId always exists */
  companyId: string | null;
  status: OfferStatus;
  type: OfferType;
  title: string;
  /** Storage path. ACC offers require this field. */
  imagePath: string;
  createDate: number;
  /** Server timestamp of the most recent deletion; absent on older records. */
  deleteDate?: number | null;
  /** User who deleted the offer, set by the server from the authenticated request. */
  deletedByUserId?: string | null;
  dateFrom: number;
  dateTo: number;
  /** Null means the offer is visible to all users in ACC. */
  targetUsers: OfferTargetUsers | null;
  tagIds: string[];
  discountCodes?: string[];
  locationName: string | null;
  eventDateFrom: number | null;
  eventDateTo: number | null;
  limitedSeating: boolean;
  seatsAvailable: number | null;
}

export interface OfferDetailsData {
  id: string;
  creatorUserId: string;
  /** one of estateId or companyId always exists */
  estateId: string | null;
  /** one of estateId or companyId always exists */
  companyId: string | null;
  lastUpdate: number;
  description: string;
  termsPath: string;
  /** Optional invitation video in Storage. Missing or null means no video. */
  videoPath?: string | null;
  linkUrl: string | null;
  locationAddress: string | null;
  locationMapUrl: string | null;
  locationTips: string | null;
  price: string | null;
  priceCurrency: string | null;
  totalSeats: number | null;
  plusOneCount: number;
}

export interface OfferSubscriptionData {
  [userId: string]: boolean;
}

export interface OfferReservationData {
  id: string;
  estateId: string | null;
  companyId: string | null;
  userId: string;
  date: number;
  plusOnes: number | null;
  deleteDate: number | null;
}

/**
 * @path /offerNote/$entityId/$offerId/$noteId
 * @database shard
 */
export interface OfferNoteData {
  id: string;
  offerId: string;
  authorUserId: string;
  authorName: string;
  content: string;
  createdAt: number;
  lastUpdate: number;
}

export interface OffersRegistrationData {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  address: string | null;
  phone: string | null;
  email: string | null;
  date: number;
  deleteDate: number | null;
}

export interface OfferTagSettingsData {
  id: string;
  label: OfferLocalizedText;
  description?: OfferLocalizedText;
  visibleInApp: boolean;
  color: string; // color in HEX
}

export interface OfferAgreementSettingsData {
  id: string;
  optional: boolean;
  text: OfferLocalizedText;
  type: string;
}

export interface OffersRegistrationAgreementData {
  userId: string;
  agreementId: string;
  date: number;
  revokeDate: number | null;
  agreementText: string;
  channel: OfferRegistrationAgreementChannel;
}

export enum OfferRegistrationAgreementChannel {
  MOBILE = 'mobile',
  EMAIL = 'email',
  ALL = 'all'
}

export interface OfferTargetUsers {
  /** False retains an explicit exclusion without turning an empty audience into a public offer. */
  [userId: string]: boolean;
}

export enum OfferStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  DELETED = 'deleted'
}

export enum OfferType {
  EVENT = 'event',
  PROMO = 'promo',
  LINK = 'link'
}
